import { defineComponent as _defineComponent } from 'vue'
import carousel from "vue-owl-carousel";
import { format, parseISO } from "date-fns";
import { computed, onMounted, ref, Ref, watch } from "vue";
import { LiveLecture } from "@/interfaces/liveLecture.interface";
import store from "@/store";
import Staff from "@/interfaces/staff.interface";
import useHttpRequest from "@/composables/use-http-request";
import { useShowWarningMessage } from "@/composables/use-notifications";
import { useRouter } from "vue-router/composables";
import { googleAuthCodeLogin } from "vue3-google-login";

import { Loading } from "element-ui";
import LinkUniversityEmailDialog from "./dialogs/link-university-email-dialog.vue";
import LecturerYouAreMeetingHostDialog from "./dialogs/lecturer-you-are-meeting-host-dialog.vue";
import EnterLectureAuthCodeDialog from "./dialogs/enter-lecture-auth-code-dialog.vue";


export default /*#__PURE__*/_defineComponent({
  __name: 'upcoming-lectures-component',
  setup(__props) {

const linkUniversityEmailDialogDialogVisible = ref(false);
const youAreMeetingHostDialogDialogVisible = ref(false);
const enterLectureAuthCodeDialogDialogVisible = ref(false);

const staffOfficialEmail = ref("");

const router = useRouter();
const preloaderCarouselRef: Ref<HTMLElement | null> = ref(null);

const isFetching = ref(true);
const isError = ref(false);
const liveLectures: Ref<LiveLecture[]> = ref([]);
const lecturerInfo: Ref<Staff> = ref({} as Staff);
const carouselKey = ref(1);
let liveLectureId: string | undefined;

const formattedDate = computed(() => {
  return (dateString: string) => format(parseISO(dateString), "do MMMM yyyy");
});

const getInitials = (first_name = "", last_name = "") => {
  let initials =
    first_name.substring(0, 1).toUpperCase() +
    last_name.substring(0, 1).toUpperCase();

  return initials;
};

watch(
  () => liveLectures.value,
  () => {
    carouselKey.value++;
  }
);

const fetchLiveLectures = async () => {
  isError.value = false;
  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    live_lectures: LiveLecture[];
    staff: Staff;
  }>({
    method: "GET",
    url: `live-lecture/fetch/lecturer/${
      store.state.userId
    }?only_up_coming&timestamp=${new Date().getTime()}`,
    loadingProperty: isFetching,
    showSuccessMessage: false,
  });

  if (!request) {
    isError.value = true;
    return;
  }

  if (request.success && request.message == "LECTURES_RETRIEVED_SUCCESSFULLY") {
    liveLectures.value = request.live_lectures;
    lecturerInfo.value = request.staff;
  } else {
    isError.value = true;
  }
};

let googleMeetUrl = "";

const goToGoogleMeetUrl = () => {
  return (window.location.href = googleMeetUrl);
};

const fetchOneLiveLectureUrl = async ({
  googleAuthCode,
  lecture,
  authCode,
}: {
  lecture?: LiveLecture;
  googleAuthCode?: Awaited<ReturnType<typeof googleAuthCodeLogin>> | undefined;
  authCode?: string;
}) => {
  if (lecture) {
    liveLectureId = lecture.live_lecture_id;
    // eslint-disable-next-line no-unreachable
    if (lecture.date_relative > 0) {
      return useShowWarningMessage(
        "Too Early",
        "The Lecture you are trying to take is still to early to take"
      );
    }
  }

  const request = await useHttpRequest<{
    success: boolean;
    message: string;
    google_meet_url: string;
    university_email?: string;
    host_status_changed?: boolean;
  }>({
    method: "POST",
    url: `live-lecture/get-lecturer-class-url/by-live-lecture-id/${liveLectureId}`,
    body: {
      googleAuthCode,
      auth_code: authCode,
    },
    loadingProperty: null,
    showSuccessMessage: false,
    showFullScreenLoader: true,
  });

  if (!request) {
    return;
  }

  if (request.success && request.message === "LIVE CLASS URL RETRIEVED") {
    //TODO: We nolonger manage this ourselves as we use google meet
    // Store the access token in the session storage
    // sessionStorage.setItem("liveClassAccessToken", request.token);
    // sessionStorage.setItem("liveClassRedirectUrl", this.$route.path);
    // sessionStorage.setItem(
    //   "liveClassEndsAt",
    //   addSeconds(new Date(), request.endsInSeconds)
    // );

    return router.push("/ongoing-lecture");
  }
  //Otherwise if its a google meet url
  else if (request.message === "Google Meet URL retrieved") {
    enterLectureAuthCodeDialogDialogVisible.value = false;
    googleMeetUrl = request.google_meet_url;

    if (request.host_status_changed) {
      youAreMeetingHostDialogDialogVisible.value = true;
    } else {
      return goToGoogleMeetUrl();
    }
  } else if (request.message === "Lecturer Google Account not linked") {
    enterLectureAuthCodeDialogDialogVisible.value = false;
    staffOfficialEmail.value = request.university_email ?? "";
    linkUniversityEmailDialogDialogVisible.value = true;
  } else if (
    request.message ===
      "Code not yet generated. Please use the My VU App to generate the code for this lecture" ||
    request.message === "Auth Code is required to start this lecture"
  ) {
    enterLectureAuthCodeDialogDialogVisible.value = true;
  } else if (
    request.message ===
    "Live lectures are only enabled in production environment"
  ) {
    enterLectureAuthCodeDialogDialogVisible.value = false;
  }
};

const requestGoogleAuthAndFetchLectureUrl = async () => {
  let loadingIndicator: ReturnType<typeof Loading.service> | undefined;

  try {
    loadingIndicator = Loading.service({
      lock: true,
      text: "Working with Google. Sign in to continue...",
      background: "rgba(0, 0, 0, 0.7)",
    });

    const googleAuthCode = await googleAuthCodeLogin({
      clientId: process.env.VUE_APP_OAUTH2CLIENT_CLIENT_ID,
    });

    if (googleAuthCode) {
      return fetchOneLiveLectureUrl({ googleAuthCode });
    }

    throw new Error("Google Auth Code not received");
  } catch (error) {
    useShowWarningMessage(
      "Google Login Failed",
      "We couldn't connect to your Google account. Please try signing in again to continue."
    );
  } finally {
    if (loadingIndicator) {
      loadingIndicator.close();
    }
  }
};

onMounted(() => {
  //@ts-expect-error $el does not exist on type HTMLElement
  const el = preloaderCarouselRef.value.$el;
  if (el) {
    const prev = el.getElementsByClassName("owl-prev")[0];
    const next = el.getElementsByClassName("owl-next")[0];

    if (prev) prev.innerHTML = "<i class='fas fa-chevron-left'></i>";
    if (next) next.innerHTML = "<i class='fas fa-chevron-right'></i>";
  }

  fetchLiveLectures();
});

return { __sfc: true,linkUniversityEmailDialogDialogVisible, youAreMeetingHostDialogDialogVisible, enterLectureAuthCodeDialogDialogVisible, staffOfficialEmail, router, preloaderCarouselRef, isFetching, isError, liveLectures, lecturerInfo, carouselKey, liveLectureId, formattedDate, getInitials, fetchLiveLectures, googleMeetUrl, goToGoogleMeetUrl, fetchOneLiveLectureUrl, requestGoogleAuthAndFetchLectureUrl, carousel, LinkUniversityEmailDialog, LecturerYouAreMeetingHostDialog, EnterLectureAuthCodeDialog }
}

})